import { logClick } from 'ff/common/SegmentAnalytics';
import {
    renderAccount,
    renderAircraft,
    renderBilling,
    renderCustomContent,
    renderDispatch,
    renderDocs,
    renderFlights,
    renderFleet,
    renderImagery,
    renderJetFuelX,
    renderLogbook,
    renderMap,
    renderOrgSettings,
    renderTrackLogs,
    renderTripAssistant,
    renderSubsectionHeader,
    renderOrg,
    renderEnterpriseDocuments,
    renderSmartGroups,
    renderFSP,
    renderRoster,
    renderFleetTracking,
    renderDirectory,
    renderCloudAhoy,
    renderPeople,
    renderReservoir,
    renderHoneywellForge,
    renderDataDownloads,
    renderOrgRecap,
    renderChecklist,
    renderAirportDirectory,
    renderSafetyManagementSystems,
    renderJra,
} from 'ff/common/components/FFMenuV2/MenuItems';
import renderDivider, {
    renderOrgTopDivider,
} from 'ff/common/components/FFMenuV2/MenuItems/Divider';
import fillSpace from 'ff/common/components/FFMenuV2/MenuItems/FillSpace';
import { RenderMenuParams } from 'ff/common/components/FFMenuV2/RenderMenuParams';
import { hasSmsLicense, hasTrackingLicense } from 'ff/common/system';

export default function RenderOrgAdminMenu(params: RenderMenuParams) {
    //NOTE: the name of each of the sections must be unique for the cache collapse state to work. (I dont know why
    // we would ever need two section to be named the same, so its prob not a big deal, but if in the future
    // that becomes a thing, will need to rework caching collapsed state.
    const OrgSection = SectionWrapper({
        name: 'Organization',
        menuItems: [
            params?.currentUser?.dispatch_tools ||
            params?.currentUser?.isFF_FspAdmin
                ? renderDispatch
                : null,
            params?.currentUser?.jeppesen_runway_analysis ? renderJra : null,
            // If an org has the Tracking license, all admins should be able to access it (they do not need the Tracking role like regular users do)
            // If an org has Dispatch but not Tracking, we still want to show admins the Tracking tab -- when they click it they will
            // be redirected to an ad to purchase Tracking
            (params?.currentUser?.dispatch_tools &&
                !params?.currentUser?.hasDispatchLowca) ||
            (params?.currentUser && hasTrackingLicense(params?.currentUser))
                ? renderFleetTracking
                : null,
            renderCloudAhoy,
            renderFSP,
            renderHoneywellForge,
            renderAircraft,
            renderFleet,
            renderDocs,
            renderCustomContent,
            renderOrg,
            renderEnterpriseDocuments,
            renderSmartGroups,
            renderSafetyManagementSystems,
            renderBilling,
            renderJetFuelX,
            renderChecklist,
            renderRoster,
            renderOrgSettings,
        ],
    });

    const FlightTools = SectionWrapper({
        name: 'Flight Tools',
        defaultCollapse: false,
        menuItems: [
            renderFlights,
            renderMap,
            renderImagery,
            //renderDocs,
            //renderAircraft,
            renderAirportDirectory,
            renderLogbook,
            renderOrgRecap,
            renderTrackLogs,
            renderPeople,
            renderReservoir,
            renderDataDownloads,
            renderDirectory,
            renderTripAssistant,
        ],
    });

    return [
        renderOrgTopDivider,
        OrgSection,
        renderDivider,
        FlightTools,
        fillSpace.bind(null, { flex: '1' }),
        renderDivider,
        renderAccount,
    ];
}

interface ISectionParams {
    name: string;
    defaultCollapse?: boolean;
    menuItems: Array<(params: RenderMenuParams) => HTMLElement | HTMLElement[]>;
}
function SectionWrapper(sectionParams: ISectionParams) {
    return (params: RenderMenuParams) => {
        const subsectionHeader = renderSubsectionHeader(sectionParams.name);
        const subSectionContents = document.createElement('div');

        subsectionHeader.addEventListener('click', () => {
            subsectionHeader.classList.toggle('sub-section-collapse');
            subSectionContents.classList.toggle('sub-section-collapse');
            const collapsed = subsectionHeader.classList.contains(
                'sub-section-collapse'
            );
            //If not collapsed we need overflow:hidden to persist during the expanding animation, otherwise everthing
            // is instantly shown and look tacky. This is a hack, but the css hack was worse so :shrug:
            if (!collapsed) {
                subSectionContents.style.overflow = 'hidden';
                setTimeout(() => {
                    subSectionContents.style.overflow = null;
                }, 301); // <-- to match the css animation of 300ms
            }
            logClick(
                subsectionHeader,
                `FFMenu, Collapse/Expand Section Height`,
                {
                    section: sectionParams.name,
                    toCollapsed: collapsed,
                }
            );
            localStorage.setItem(
                `sub-section-collapse_${sectionParams.name}`,
                subsectionHeader.classList
                    .contains('sub-section-collapse')
                    .toString()
            );
        });

        subSectionContents.classList.add('sub-section-contents');
        sectionParams.menuItems
            .filter(Boolean)
            .map(d => d(params))
            .filter(d => d)
            .flat()
            .forEach(subSectionContents.appendChild.bind(subSectionContents));

        const cachedCollapseState = localStorage.getItem(
            `sub-section-collapse_${sectionParams.name}`
        );
        if (cachedCollapseState) {
            if (cachedCollapseState === 'true') {
                subsectionHeader.classList.toggle('sub-section-collapse');
                subSectionContents.classList.toggle('sub-section-collapse');
            }
        }
        //Can't combine this if stmt into one bc what if cachedCollapseState exists, and its false. Then we dont
        // want the defaultCollapse param to take effect.
        else if (sectionParams.defaultCollapse) {
            subsectionHeader.classList.toggle('sub-section-collapse');
            subSectionContents.classList.toggle('sub-section-collapse');
        }

        return [subsectionHeader, subSectionContents];
    };
}
